/**
 * Daily Summary Service
 * Generates daily summary of upcoming events
 */

import type { Note } from '../types/note';

export interface DailySummary {
    todayEvents: Note[];
    upcomingEvents: Note[];
    totalToday: number;
    totalUpcoming: number;
}

/**
 * Generate daily summary from notes
 */
export const generateDailySummary = (notes: Note[]): DailySummary => {
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];

    const nextWeek = new Date(today);
    nextWeek.setDate(nextWeek.getDate() + 7);
    const nextWeekStr = nextWeek.toISOString().split('T')[0];

    const todayEvents = notes.filter(n => n.date === todayStr);
    const upcomingEvents = notes.filter(n => n.date > todayStr && n.date <= nextWeekStr);

    return {
        todayEvents,
        upcomingEvents,
        totalToday: todayEvents.length,
        totalUpcoming: upcomingEvents.length,
    };
};

/**
 * Format summary text for notification
 */
export const formatSummaryText = (summary: DailySummary): string => {
    const parts: string[] = [];

    if (summary.totalToday > 0) {
        parts.push(`${summary.totalToday} sự kiện hôm nay`);
    }

    if (summary.totalUpcoming > 0) {
        parts.push(`${summary.totalUpcoming} sắp tới trong tuần`);
    }

    if (parts.length === 0) {
        return 'Không có sự kiện nào';
    }

    return parts.join(' • ');
};

/**
 * Check if should send daily summary (at configured time)
 */
export const shouldSendDailySummary = (
    lastSentDate: string | null,
    targetTime: string = '09:00'
): boolean => {
    const now = new Date();
    const today = now.toISOString().split('T')[0];

    // Already sent today
    if (lastSentDate === today) {
        return false;
    }

    // Check if current time is after target time
    const [targetHour, targetMinute] = targetTime.split(':').map(Number);
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();

    if (currentHour > targetHour || (currentHour === targetHour && currentMinute >= targetMinute)) {
        return true;
    }

    return false;
};

export default {
    generateDailySummary,
    formatSummaryText,
    shouldSendDailySummary,
};
